// ClientsPayments only. Shared table methods are in tableMixin.js.
// Methods here with the same name as a tableMixin method replace it on this page.
import axios from "axios";

export default {
    name: "ClientsPayments",
    computed: {
        // Client and Method filters on the Filter button (tableMixin counts the rest)
        pageFilterCount() {
            const t = this.tableData;
            return (t.clientId ? 1 : 0) + (t.method ? 1 : 0);
        },
        // Client and Method labels in the "Filtered by:" row (tableMixin adds the rest)
        pageFilterChips() {
            const t = this.tableData;
            const chips = [];
            if (t.clientId) {
                const client = this.clientsOptions.find((c) => Number(c.id) === Number(t.clientId));
                chips.push({ key: "client", icon: "fa fa-user", label: `Client: ${client?.label || "#" + t.clientId}` });
            }
            if (t.method) {
                chips.push({ key: "method", icon: "fa fa-credit-card", label: `Method: ${this.methodLabel(t.method)}` });
            }
            return chips;
        },
    },
    methods: {
        /* ---------------- Messages ---------------- */
        // The server sometimes sends the message as a list; show it as one line
        messageText(message) {
            return Array.isArray(message) ? message.join(" ") : message;
        },

        /* ---------------- Dates and money ---------------- */
        // DB dates are UTC "Y-m-d H:i:s"
        utcToDate(value) {
            return new Date(String(value).replace(" ", "T") + "Z");
        },
        // Value for <input type="datetime-local"> in the user's own time
        toInputValue(date) {
            const d = new Date(date);
            d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
            return d.toISOString().slice(0, 16);
        },
        nowForInput() {
            return this.toInputValue(new Date());
        },
        formatDate(value) {
            return value ? this.utcToDate(value).toLocaleString() : "-";
        },
        formatMoney(value) {
            const n = Number(value);
            return isNaN(n) ? value : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },

        /* ---------------- Labels and badges ---------------- */
        // Trial and free-days rows have no money and can't be edited
        isFreeEntry(record) {
            return ["compensation", "trial"].includes(record.payment_method);
        },
        methodLabel(value) {
            return this.allMethodOptions.find((o) => o.value === value)?.label || value || "-";
        },
        methodClass(value) {
            return ["compensation", "trial"].includes(value) ? "badge bg-info text-dark" : "badge bg-secondary";
        },
        statusLabel(value) {
            return this.statusOptions.find((o) => o.value === value)?.label || value || "-";
        },
        statusClass(value) {
            const map = {
                completed: "badge bg-success",
                pending: "badge bg-warning text-dark",
                failed: "badge bg-danger",
                refunded: "badge bg-dark",
            };
            return map[value] || "badge bg-secondary";
        },
        periodLabel(months) {
            return this.periodOptions.find((o) => o.value === Number(months))?.label || `${months} month(s)`;
        },
        // What the payment was for
        forLabel(record) {
            const meta = record.meta || {};
            if (meta.type === "compensation") return `+${meta.days} free day(s)`;
            if (meta.type === "trial") return `${meta.package_name} · ${meta.days}-day free trial`;
            if (meta.type === "subscription") return `${meta.package_name} · ${this.periodLabel(meta.period_months)}`;
            return "Payment only";
        },

        /* ---------------- Filters (this page's own) ---------------- */
        // Client and Method, sent with every request (tableMixin adds the dates)
        pageFilterParams() {
            const params = {};
            if (this.tableData.clientId) params.clientId = this.tableData.clientId;
            if (this.tableData.method) params.method = this.tableData.method;
            return params;
        },
        // × on the Client / Method label
        removePageFilterChip(key) {
            if (key === "client") this.tableData.clientId = null;
            if (key === "method") this.tableData.method = "";
        },
        // Search By changed: placeholder uses the words in the list (e.g. "Payment Number")
        changePlaceholder() {
            const label = this.searchByOptions.find((o) => o.value === this.tableData.searchBy)?.label || "";
            this.tableData.searchPlaceHolderText = `Search by ${label}`;

            // After the modal has closed, put the cursor in the search box
            document.getElementById("filterDataModal")?.addEventListener("hidden.bs.modal", () => {
                document.getElementById("search")?.focus();
            }, { once: true });

            this.$hideModal("filterDataModal");

            if (this.tableData.searchQuery) this.onSearchInput();
        },

        /* ---------------- Forms ---------------- */
        resetFormState() {
            this.submitted = false;
            this.defaultAlert.pageOrModal = "";
            this.$clearValidationStates("#recordForm");
            this.$clearValidationStates("#compensationForm");
        },
        // Record Payment button: empty form
        openNewPayment() {
            this.isEditing = false;
            this.editingRecord = null;
            this.formInputs = this.getDefaultFormInputs();
            this.lastCurrencyClientId = null; // the first client picked fills in the currency
            this.resetFormState();
        },
        // Add Free Days: empty form (or this client already picked)
        openCompensation(record = null) {
            this.formInputs = { ...this.getDefaultFormInputs(), client_id: record ? Number(record.owner_user_id) : "" };
            this.resetFormState();
        },
        // Fill the amount with plan price x months (only if the user hasn't typed one)
        onPlanInputsChange() {
            // A client was picked: their currency (KES in Kenya, USD elsewhere). Only once per client,
            // so a currency you typed yourself stays when you change the plan or period
            if (!this.isEditing && this.selectedClient && this.formInputs.client_id !== this.lastCurrencyClientId) {
                this.formInputs.currency = this.selectedClient.currency || "KES";
                this.lastCurrencyClientId = this.formInputs.client_id;
            }
            // Then the amount: plan price x months, in that currency (only if you haven't typed your own)
            const amount = this.formInputs.amount;
            if (this.suggestedAmount !== null && (amount === "" || amount === null || amount === this.lastSuggestedAmount)) {
                this.formInputs.amount = this.suggestedAmount;
            }
            this.lastSuggestedAmount = this.suggestedAmount;
            if (this.selectedClient && !this.formInputs.phone_number) {
                this.formInputs.phone_number = this.selectedClient.phone || "";
            }
        },

        /* ---------------- After the server answers ---------------- */
        // After the table loads (tableMixin calls this): clear ticks, keep a refreshed login token
        afterFetch(response) {
            this.fetchSummary(); // tiles = the same payments as the table
            this.tableData.selectedRecordIds = [];
            this.tableData.selectAll = false;
            if (response.data.results.token) localStorage.setItem("jwt", response.data.results.token);
        },
        // After a delete / restore (tableMixin calls this): summary and client plan dates change too
        afterDelete() {
            this.fetchSummary(true);
            this.fetchClientsOptions();
        },
        // Table, monthly summary and client plan dates all change after a payment
        async refreshAll() {
            this.lastSummaryKey = ""; // a payment changed: the tiles reload with the table
            this.fetchClientsOptions();
            await this.fetchRecords(); // waits for the table, so the saved row can blink
        },
        async handleSaveOrEditSuccess(response) {
            this.$hideModal("recordFormModal");
            this.handleAlert("ending", this.messageText(response.data.message), "success", "page");
            if (!this.isEditing) this.tableData.currentPage = 1;
            await this.refreshAll();
            this.flashRow(response.data.results?.id); // tableMixin: the saved payment's row blinks
        },
        async handleCompensationSuccess(response) {
            this.$hideModal("compensationModal");
            this.handleAlert("ending", this.messageText(response.data.message), "success", "page");
            this.tableData.currentPage = 1;
            await this.refreshAll();
            this.flashRow(response.data.results?.id);
        },
        // "Delete Selected": warns that plans don't change
        deleteSelectedRecords() {
            const ids = this.tableData.selectedRecordIds;
            if (!ids.length) return;
            if (!confirm(`Delete ${ids.length} payment(s)? This hides them from reports but does not change any plan.`)) return;

            this.$showOrHideLoader(this.$loaderMessages.deleting);
            axios
                .delete(this.apiUrl, this.$handleConfig({ action: "deleteRecord", ids, deleteType: "soft" }))
                .then((response) => {
                    if (response.data.status === "success") {
                        this.handleAlert("ending", this.messageText(response.data.message), "success", "page");
                        this.refreshAll();
                    } else {
                        this.tableData.isVisible = false;
                        this.handleAlert("ending", this.messageText(response.data.message), "danger", "page");
                    }
                })
                .catch((error) => this.handleRequestError(error, "page"));
        },
    },
}; 